import { useCallback, useEffect, useRef, useState } from "react"
import { useSearchParams } from "react-router-dom"
import { useMessage } from "hooks/message.hook"
import {
	POP_OUT_OWNER_PARAM,
	createOpenerId,
	openPopOutControlChannel,
	type TPopOutControlChannel,
} from "helpers/popOutWindowChannel.helper"
import { usePopOutWindowStore } from "store/popOutWindow.store"
import type { TViewerMode } from "store/viewer.store"
import {
	POP_OUT_WINDOW_NAME,
	buildPopOutWindowUrl,
	fallbackPlacement,
	isPopOutWindowRoute,
	resolveViewerSearch,
	resolveWindowPlacement,
	toWindowFeatures,
} from "../core/popOutWindow.helpers"

// The quotation editor's second view in a REAL browser window — #1430.
//
// The in-window dual view lives inside one browser window, so it can never be dragged onto a
// second monitor. This can: `window.open` on our own `/quotation-window` route produces an actual
// OS window the user can throw anywhere.
//
// Why a route rather than injecting into `about:blank`: the child boots its own copy of the app,
// with its own document, JS realm and module registry. That is what makes a SECOND Apryse instance
// legal here at all — #1119 ("a 2nd WebViewer permanently breaks the 1st instance's
// click-to-select") is a statement about two instances sharing ONE realm. Two realms cannot
// collide that way.
//
// The two windows do not share a TanStack cache, so they reconcile through the server via
// `helpers/quotationWindowSync.helper.ts` — see its header for why no state is ever sent across.
// Intent (who is open, "bring the view back") travels on a separate control channel,
// `helpers/popOutWindowChannel.helper.ts`.
//
// Auth needs no plumbing: the token is a cookie on this origin, so the child authenticates itself.
//
// THIS HOOK RUNS IN BOTH WINDOWS. The orchestrator is the same code either side, so every branch
// below is written for both roles: the opener owns the window handle and the poll, the child owns
// its own "I am the pop-out" state and asks the opener to take the view back. `isPopOutWindowRoute()`
// is what separates them, and it is constant for the window's whole life.

// Set to exactly "off" to hide the pop-out row. Mirrors `VITE_SPLIT_SCREEN` so the two viewer
// layout features can be rolled out independently. Exported because the MENU has to be gated on it
// too, and the header's `render()` captures its props once — only a value that is constant for the
// session can be a prop.
export const POP_OUT_WINDOW_ENABLED = import.meta.env.VITE_POP_OUT_WINDOW !== "off"

// How often the opener checks whether the pop-out was closed from its own title bar. There is no
// cross-window "I closed" event we can rely on (`pagehide` in the child is best-effort and does not
// fire on a crash or a force-quit), so the menu's state is polled as a floor under the control
// channel's `pop-out-closed` note, which is the fast path.
const CLOSED_POLL_MS = 700

export type TPopOutWindowContext = {
	mode: TViewerMode
	isDocumentLoaded: boolean
	drawingStatus: string
	// The in-window dual view, so popping out can hand over from it and returning can hand back.
	// Both come from `useSplitScreen` — the ONE guarded entry point for the mode — rather than being
	// re-implemented here.
	isSplitActive: boolean
	setSplit: (active: boolean) => void
	// Collapses an active split onto the LEFT pane, keeping its page/zoom/scroll. Used when popping
	// out: the second view is moving to its own window, so the pane the user was reading must not
	// jump (#1430 AC8). `setSplit(false)` alone does not promise that — see useSplitScreen.
	closeRightView: () => void
}

export const usePopOutWindow = ({
	mode,
	isDocumentLoaded,
	drawingStatus,
	isSplitActive,
	setSplit,
	closeRightView,
}: TPopOutWindowContext) => {
	const [searchParams] = useSearchParams()
	const { error: showError } = useMessage()
	const isPopOutOpen = usePopOutWindowStore((state) => state.isPopOutOpen)
	const isPopOutPending = usePopOutWindowStore((state) => state.isPopOutPending)
	const setPopOutOpen = usePopOutWindowStore((state) => state.setPopOutOpen)
	const setPopOutPending = usePopOutWindowStore((state) => state.setPopOutPending)
	const setPopOutAvailable = usePopOutWindowStore((state) => state.setPopOutAvailable)

	// Constant for this window's life, so it is safe to read outside React's flow (inside the
	// channel handler below, which is bound once).
	const isChildWindow = isPopOutWindowRoute()

	// Which editor session this window's control traffic belongs to. The OPENER mints one; the CHILD
	// takes the opener's, handed to it in the pop-out URL, so the two share an address without a
	// handshake. Everything on the channel is filtered by it — see the header of
	// popOutWindowChannel.helper for the two-tabs failure it prevents.
	//
	// Read via `resolveViewerSearch()`, NOT a plain `window.location.search` — this hook mounts well
	// after `PageQuotationWindow` has already stripped the child's query string (it is gated behind
	// two async fetches), so the live URL never has the opener id by the time this runs.
	// `resolveViewerSearch()` is the same fallback-to-sessionStorage mechanism the other pop-out ids
	// already use for exactly this race (see `TPopOutWindowParams.openerId`'s own comment).
	//
	// Minted once per mount via a lazy initializer: a fresh id on every render would change the
	// address mid-session and orphan a live pop-out.
	const [openerId] = useState(
		() => (isChildWindow ? new URLSearchParams(resolveViewerSearch()).get(POP_OUT_OWNER_PARAM) : null) ?? createOpenerId(),
	)

	const childRef = useRef<Window | null>(null)
	const pollRef = useRef<number | null>(null)
	const channelRef = useRef<TPopOutControlChannel | null>(null)
	// The channel handler is bound ONCE, so anything it needs to call has to arrive by ref — a
	// captured `setSplit` would be the one from the render that happened to bind it, and
	// `useSplitScreen` re-creates it on every state change.
	const setSplitRef = useRef(setSplit)
	setSplitRef.current = setSplit

	const stopPolling = useCallback(() => {
		if (pollRef.current == null) return
		window.clearInterval(pollRef.current)
		pollRef.current = null
	}, [])

	// Drop the handle and the poll, and report the pop-out as gone. Shared by every path that ends
	// the pop-out — the row, the poll, the peer's own notice, teardown — so none of them can leave
	// half the state behind.
	const forgetChild = useCallback(() => {
		stopPolling()
		childRef.current = null
		setPopOutOpen(false)
	}, [setPopOutOpen, stopPolling])

	const closePopOut = useCallback(() => {
		const child = childRef.current
		forgetChild()
		try {
			if (child && !child.closed) child.close()
		} catch {
			// Cross-origin or already-torn-down handle; the poll would have cleared it anyway.
		}
		// Belt for the close above — reaches a pop-out this window no longer holds a handle to.
		channelRef.current?.post({ type: "pop-out-close-requested" })
	}, [forgetChild])

	const watchForClose = useCallback(() => {
		stopPolling()
		pollRef.current = window.setInterval(() => {
			if (childRef.current && !childRef.current.closed) return
			forgetChild()
		}, CLOSED_POLL_MS)
	}, [forgetChild, stopPolling])

	// One control channel per window, opened for the life of the editor. Both roles listen on it;
	// which messages matter depends on the role, which is why the handler branches on
	// `isChildWindow` rather than two hooks doing half the work each.
	useEffect(() => {
		const channel = openPopOutControlChannel(openerId, (message) => {
			switch (message.type) {
				case "pop-out-opened":
					// Only the opener cares: the child already knows what it is.
					if (!isChildWindow) setPopOutOpen(true)
					break
				case "pop-out-closed":
					if (!isChildWindow) forgetChild()
					break
				case "return-view-back":
					// The child asked the opener to take the view back. The child closes itself, so all
					// this side has to do is forget it and re-enter the in-window split. Guarded on role:
					// a child must never try to enter a split it is not allowed to have.
					if (isChildWindow) break
					forgetChild()
					setSplitRef.current(true)
					break
				case "pop-out-close-requested":
					if (isChildWindow) window.close()
					break
			}
		})
		channelRef.current = channel
		// Announce ourselves so the opener's menu reaches its popped-out state even if `window.open`
		// handed back an unusable reference (some pop-up blockers return a window that never loads).
		if (isChildWindow) channel.post({ type: "pop-out-opened" })
		return () => {
			channelRef.current = null
			channel.close()
		}
	}, [forgetChild, isChildWindow, openerId, setPopOutOpen])

	// The child IS the pop-out, so its own menu must be in the popped-out state from its first
	// render — that is what turns row 2 into `Return view back` and leaves `Dual view` inert.
	useEffect(() => {
		if (isChildWindow) setPopOutOpen(true)
	}, [isChildWindow, setPopOutOpen])

	// Tell the opener the moment this window goes away, so its menu snaps back rather than waiting
	// out the poll. `pagehide` rather than `beforeunload`: it also fires when the window is
	// discarded or enters the back/forward cache.
	useEffect(() => {
		if (!isChildWindow) return
		const onGone = () => channelRef.current?.post({ type: "pop-out-closed" })
		window.addEventListener("pagehide", onGone)
		return () => window.removeEventListener("pagehide", onGone)
	}, [isChildWindow])

	const openPopOut = useCallback(() => {
		// Already up: focus it rather than opening a second one. Two children would mean two more
		// Apryse instances and two more copies of the drawing in memory.
		if (childRef.current && !childRef.current.closed) {
			childRef.current.focus()
			return
		}

		const drawingId = Number(searchParams.get("drawingID"))
		if (!drawingId) {
			showError("Couldn't open the view in a new window — the drawing is not identified.")
			return
		}
		const url = buildPopOutWindowUrl(
			window.location.origin,
			{
				drawingId,
				processingId: Number(searchParams.get("processingID")) || null,
				quotationId: Number(searchParams.get("quotationID")) || null,
				drawingStatus,
			},
			openerId,
		)

		// Hand over from the in-window split BEFORE opening, keeping the left pane's page/zoom/scroll
		// (#1430 AC8): the second view is moving to its own window, so there is nothing for the right
		// pane to show, and collapsing afterwards would make the drawing jump under a user who has
		// just looked away at the new window. Synchronous — it only schedules its own restore — so it
		// does not spend the click's activation token.
		if (isSplitActive) closeRightView()

		setPopOutPending(true)
		// Open SYNCHRONOUSLY, on the click's user-activation token. Resolving the monitor layout
		// first and opening after the await looks tidier but is broken: `getScreenDetails()` blocks
		// on a permission prompt, and ANY await spends the activation, so the browser treats the
		// `window.open` as unsolicited and blocks it. Verified on the PoC — the awaited version
		// opened nothing at all.
		//
		// So: open at a safe fallback size, then refine the placement once the layout is known.
		//
		// Wrapped because ANY throw between `setPopOutPending(true)` and the settle below would leave
		// pending stuck true — and pending disables the only row that could retry, for the rest of the
		// session. The store clears pending on every `setPopOutOpen`, but that only covers the paths
		// that reach it.
		let child: Window | null = null
		try {
			child = window.open(url, POP_OUT_WINDOW_NAME, toWindowFeatures(fallbackPlacement(window.screen)))
		} catch (error) {
			console.error("Failed to open the pop-out window:", error)
		}

		if (!child) {
			setPopOutPending(false)
			showError("Your browser blocked the new window. Allow pop-ups for this site and try again.")
			return
		}
		// Narrowed for the closures below, which TypeScript cannot see past the `let`.
		const popOutWindow = child

		// Best-effort move onto the other monitor, which is the whole point of a real window. Async
		// and unawaited: if the user denies the permission (or never answers the prompt) the window
		// simply stays where it opened, which is still perfectly usable.
		void resolveWindowPlacement(window).then((placement) => {
			try {
				if (popOutWindow.closed) return
				popOutWindow.moveTo(Math.round(placement.left), Math.round(placement.top))
				popOutWindow.resizeTo(Math.round(placement.width), Math.round(placement.height))
			} catch {
				// Some browsers refuse moveTo/resizeTo on a window they consider a tab. Harmless.
			}
		})

		childRef.current = popOutWindow
		setPopOutOpen(true)
		popOutWindow.focus()
		watchForClose()
	}, [
		closeRightView,
		drawingStatus,
		isSplitActive,
		openerId,
		searchParams,
		setPopOutOpen,
		setPopOutPending,
		showError,
		watchForClose,
	])

	// "Return right view back" (opener) / "Return view back" (child) — one row, one name per side,
	// one behaviour: the pop-out goes away and the second view reappears in the main window.
	//
	// From the CHILD this cannot be done locally: entering the in-window split is the opener's job,
	// in the opener's realm, through the opener's own `useSplitScreen`. So the child asks, then
	// closes itself; the opener's `return-view-back` branch above does the rest.
	const returnViewBack = useCallback(() => {
		if (isChildWindow) {
			channelRef.current?.post({ type: "return-view-back" })
			window.close()
			return
		}
		closePopOut()
		setSplit(true)
	}, [closePopOut, isChildWindow, setSplit])

	// The child never offers to pop out again — `window.open` with our named target, called from the
	// window that already HOLDS that name, would navigate this window onto itself rather than open a
	// third. The remaining conditions mirror the in-window split's: an editor with a loaded document,
	// and a drawing we can actually identify.
	const canPopOut =
		POP_OUT_WINDOW_ENABLED &&
		!isChildWindow &&
		mode === "editor" &&
		isDocumentLoaded &&
		!!Number(searchParams.get("drawingID"))

	// Published for the header's menu, which cannot take it as a prop (captured once).
	useEffect(() => {
		setPopOutAvailable(canPopOut)
	}, [canPopOut, setPopOutAvailable])

	// The pop-out is an editor affordance. Leaving editor mode (switching to preview) or closing the
	// document closes it: a window left orphaned behind a closed editor holds a second Apryse
	// instance and a second copy of the drawing, with no menu left anywhere to close it from.
	useEffect(() => {
		if (isChildWindow) return
		if ((mode !== "editor" || !isDocumentLoaded) && childRef.current) closePopOut()
	}, [closePopOut, isChildWindow, isDocumentLoaded, mode])

	// Closing the opener must not leave the child stranded, for the same reason. `pagehide` rather
	// than `beforeunload`: it also fires when the tab is discarded or enters the back/forward cache.
	useEffect(() => {
		if (isChildWindow) return
		const onOpenerGone = () => {
			try {
				childRef.current?.close()
			} catch {
				// Nothing to do while the opener itself is being torn down.
			}
		}
		window.addEventListener("pagehide", onOpenerGone)
		return () => window.removeEventListener("pagehide", onOpenerGone)
	}, [isChildWindow])

	// Teardown: drop the poll, close any child, and reset the store so a reopened editor starts from
	// a clean state rather than inheriting a stale "popped out".
	useEffect(() => {
		return () => {
			stopPolling()
			try {
				childRef.current?.close()
			} catch {
				// See above.
			}
			childRef.current = null
			usePopOutWindowStore.setState({
				isPopOutOpen: false,
				isPopOutPending: false,
				isPopOutAvailable: false,
			})
		}
	}, [stopPolling])

	const popOut = useCallback(() => {
		if (isPopOutPending) return
		// Already out: bring it forward rather than doing nothing. The row is not offered in this
		// state (it reads `Return right view back` instead), so this is the digit/hotkey path.
		if (isPopOutOpen) {
			childRef.current?.focus()
			return
		}
		if (!canPopOut) return
		openPopOut()
	}, [canPopOut, isPopOutOpen, isPopOutPending, openPopOut])

	return {
		isPopOutOpen,
		isPopOutPending,
		canPopOut,
		popOut,
		returnViewBack,
		closePopOut,
		// Published for usePopOutFollowNavigation, which needs the exact same channel address —
		// constant for the window's life, same as it is here.
		openerId,
	}
}
